import type { ClassValue } from 'clsx';

import {
  FreeShippingProgress,
  FreeShippingProgressAmount,
  FreeShippingProgressLabel,
  FreeShippingProgressNotice,
  FreeShippingProgressRemaining,
  FreeShippingProgressTrack,
} from '@vs-shop/commerce-ui/freeShippingProgress';

import { clsxm } from '@vs-shop/utils';

import type { CurrencyT } from '../../../../config/currency';

import { getMemoizedIntlShapeWithDefaultLocale } from '../../../../utils/intl';

import { IntlMoney } from '../../intl/Intl';

type Props = {
  threshold: number;
  remaining: number;
  currency: CurrencyT;
  className?: ClassValue;
};

/** This tenant supplies the free-shipping copy and currency. */
export default function FreeShippingProgressBar({
  threshold,
  remaining,
  currency,
  className,
}: Props) {
  const intl = getMemoizedIntlShapeWithDefaultLocale();

  const currentAmount = threshold - remaining;
  const progressPercentage =
    threshold === 0 ? 100 : Math.min((currentAmount / threshold) * 100, 100);

  const isShippingFree = remaining === 0;

  return (
    <FreeShippingProgress className={clsxm(className)}>
      {isShippingFree ? (
        <FreeShippingProgressNotice>
          {intl.formatMessage({
            id: 'freeShippingProgressBar.isShippingFree.true',
            defaultMessage: 'Dopravu máte zdarma',
          })}
        </FreeShippingProgressNotice>
      ) : (
        <FreeShippingProgressRemaining>
          <FreeShippingProgressLabel>
            {intl.formatMessage({
              id: 'freeShippingProgressBar.isShippingFree.false',
              defaultMessage: 'Do dopravy zdarma vám ještě zbývá',
            })}
          </FreeShippingProgressLabel>
          <FreeShippingProgressAmount>
            <IntlMoney value={{ amount: remaining, currency: currency }} />
          </FreeShippingProgressAmount>
        </FreeShippingProgressRemaining>
      )}
      <FreeShippingProgressTrack value={isShippingFree ? 100 : progressPercentage} />
    </FreeShippingProgress>
  );
}
